import React, { useState, useEffect } from 'react';
import { Bell, Save, AlertCircle, Info, RefreshCw, MessageSquare, Heart, Star, Users, Eye, CheckCircle, XCircle, List, Video, Gift } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { Badge } from '../ui/badge';

interface NotificationSettings {
  messages: boolean;
  interests: boolean;
  superInterests: boolean;
  acceptedInterests: boolean;
  rejectedInterests: boolean;
  likes: boolean;
  superLikes: boolean;
  matches: boolean;
  profileViews: boolean;
  shortlists: boolean;
  videoCalls: boolean;
  referralSignup: boolean;
}

const notificationTypes = [
  {
    key: 'messages' as keyof NotificationSettings,
    label: 'Messages',
    description: 'New message notifications',
    icon: MessageSquare,
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  {
    key: 'interests' as keyof NotificationSettings,
    label: 'Interests',
    description: 'When someone sends an interest',
    icon: Heart,
    color: 'text-red-600',
    bgColor: 'bg-red-50'
  },
  {
    key: 'superInterests' as keyof NotificationSettings,
    label: 'Super Interests',
    description: 'When someone sends a super interest',
    icon: Star,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50'
  },
  {
    key: 'acceptedInterests' as keyof NotificationSettings,
    label: 'Interest Accepted',
    description: 'When someone accepts your interest',
    icon: CheckCircle,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50'
  },
  {
    key: 'rejectedInterests' as keyof NotificationSettings,
    label: 'Interest Declined',
    description: 'When someone declines your interest',
    icon: XCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50'
  },
  {
    key: 'likes' as keyof NotificationSettings,
    label: 'Likes',
    description: 'When someone likes your profile',
    icon: Heart,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50'
  },
  {
    key: 'superLikes' as keyof NotificationSettings,
    label: 'Super Likes',
    description: 'When someone super likes your profile',
    icon: Star,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50'
  },
  {
    key: 'matches' as keyof NotificationSettings,
    label: 'Matches',
    description: 'When you match with someone',
    icon: Users,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  {
    key: 'profileViews' as keyof NotificationSettings,
    label: 'Profile Views',
    description: 'When someone views your profile',
    icon: Eye,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  {
    key: 'shortlists' as keyof NotificationSettings,
    label: 'Shortlists',
    description: 'When someone adds you to shortlist',
    icon: List,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50'
  },
  {
    key: 'videoCalls' as keyof NotificationSettings,
    label: 'Video Calls',
    description: 'When someone initiates a video call',
    icon: Video,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50'
  },
  {
    key: 'referralSignup' as keyof NotificationSettings,
    label: 'Referral Signup',
    description: 'When a user signs up through a referral link',
    icon: Gift,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50'
  }
];

export default function AdminPushNotificationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<NotificationSettings>({
    messages: true,
    interests: true,
    superInterests: true,
    acceptedInterests: true,
    rejectedInterests: true,
    likes: true,
    superLikes: true,
    matches: true,
    profileViews: true,
    shortlists: true,
    videoCalls: true,
    referralSignup: true
  });
  const [originalSettings, setOriginalSettings] = useState<NotificationSettings>(settings);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API_URL}/admin/settings/push-notifications`);

      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        setOriginalSettings(response.data.data);
        toast.success('Settings loaded successfully');
      }
    } catch (error: any) {
      toast.info('Using default settings (all enabled)');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.put(`${API_URL}/admin/settings/push-notifications`, settings);

      if (response.data.success) {
        toast.success('Push notification settings updated successfully');
        setOriginalSettings(settings);
        setHasChanges(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
    setHasChanges(false);
  };

  const handleEnableAll = () => {
    setSettings({
      messages: true,
      interests: true,
      superInterests: true,
      acceptedInterests: true,
      rejectedInterests: true,
      likes: true,
      superLikes: true,
      matches: true,
      profileViews: true,
      shortlists: true,
      videoCalls: true,
      referralSignup: true
    });
  };

  const handleDisableAll = () => {
    setSettings({
      messages: false,
      interests: false,
      superInterests: false,
      acceptedInterests: false,
      rejectedInterests: false,
      likes: false,
      superLikes: false,
      matches: false,
      profileViews: false,
      shortlists: false,
      videoCalls: false,
      referralSignup: false
    });
  };

  const enabledCount = Object.values(settings).filter(v => v).length;
  const totalCount = Object.keys(settings).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Push Notification Settings</h2>
        <p className="text-gray-600 mt-1">
          Control which types of push notifications are sent to users
        </p>
        {/* Current Status Badge */}
        <div className="mt-3 flex items-center gap-3">
          <Badge className={enabledCount === totalCount ? 'bg-green-600' : enabledCount === 0 ? 'bg-gray-600' : 'bg-blue-600'}>
            {enabledCount} of {totalCount} Enabled
          </Badge>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleEnableAll}
              disabled={enabledCount === totalCount}
            >
              Enable All
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDisableAll}
              disabled={enabledCount === 0}
            >
              Disable All
            </Button>
          </div>
        </div>
      </div>

      {/* Notification Type Cards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-rose-600" />
            Notification Types
          </CardTitle>
          <CardDescription>
            Enable or disable specific notification types globally for all users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {notificationTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div
                  key={type.key}
                  className={`flex items-start justify-between p-4 border rounded-lg transition-all ${
                    settings[type.key] ? 'bg-white border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex-1 space-y-2 pr-4">
                    <div className="flex items-center gap-3">
                      <div className={`${type.bgColor} rounded-lg p-2`}>
                        <Icon className={`h-5 w-5 ${type.color}`} />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{type.label}</span>
                        {settings[type.key] && (
                          <Badge className="ml-2 bg-green-500 text-white text-xs border-0 px-2 py-0.5">Active</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 ml-12">{type.description}</p>
                  </div>
                  <Switch
                    checked={settings[type.key]}
                    onCheckedChange={() => handleToggle(type.key)}
                    disabled={saving}
                  />
                </div>
              );
            })}
          </div>

          {/* Info Box */}
          <Card className="bg-blue-50 border-blue-200 mt-6">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 space-y-2">
                  <p className="font-medium">How Notification Settings Work:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-800 ml-2">
                    <li><strong>Global Control:</strong> When disabled here, NO users will receive that type of notification</li>
                    <li><strong>User Preferences:</strong> Users can further control their own notification preferences in their settings</li>
                    <li><strong>Immediate Effect:</strong> Changes take effect immediately for all new notifications</li>
                    <li><strong>Database Tracking:</strong> Notification events are still recorded even when notifications are disabled</li>
                    <li><strong>Admin Override:</strong> These settings override individual user preferences</li>
                    <li><strong>Profile Views:</strong> Includes when users view profiles (toggle on/off as needed)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              {hasChanges && (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-900">You have unsaved changes</span>
                </>
              )}
            </div>
            <div className="flex gap-3">
              {hasChanges && (
                <Button variant="outline" onClick={handleReset} disabled={saving}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="bg-rose-600 hover:bg-rose-700"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}